import { useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import { resolveSuggestedTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { LEAD_AGENT_PAYWALL_EVENTS } from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';
import { formatPaymentDate } from '../../LeadAgentAcceptanceBanner.functions';
import type { tDeclineModalProps } from './DeclineModal.types';

const NS = 'common/leadAgentAcceptanceModals';

type tDeclineStep = 'warning' | 'confirmed';

/**
 * Lead Agent decline / pause modal (RET-1230 / C2, Unit 6). Two internal steps
 * faithful to Fanny's prototype:
 *  - warning: what the Owner loses on the personal renewal date, with
 *    "Mejor mantenerlo" (keep, closes) and "Sí, no lo quiero" (pause).
 *  - confirmed: post-PATCH PAUSED confirmation with "Reactivar ahora" (reopens
 *    the confirm-tier flow, PAUSED -> ACCEPTED is legal) and "Volver al
 *    dashboard" (clean dashboard; the banner already self-hid via PAUSED).
 *
 * Emits `la_paywall_paused_clicked` on the pause CLICK (intent metric, D8
 * plan): it fires before the PATCH and is not gated on the PATCH succeeding.
 */
export default function DeclineModal(props: tDeclineModalProps) {
  const { onClose, onReactivate } = props;
  const { t } = useTranslation(NS);
  const { addAlert } = useToastContext();
  const { trackEvent } = useAnalytics();
  const { acceptance, pauseAcceptance, markResolved } =
    useLeadAgentAcceptanceContext();

  const [step, setStep] = useState<tDeclineStep>('warning');
  const [submitting, setSubmitting] = useState(false);
  // Synchronous in-flight latch. `submitting` is async React state, so two
  // same-tick clicks would both pass and fire two PATCHes plus two
  // `paused_clicked` events. This ref flips synchronously so the 2nd bails.
  const inFlightRef = useRef(false);

  const suggestedTier = resolveSuggestedTier(acceptance?.suggestedTier);

  const paymentDate = useMemo(
    () => formatPaymentDate(acceptance?.nextPaymentAt ?? null),
    [acceptance?.nextPaymentAt],
  );

  async function handleDecline() {
    // Synchronous double-click guard (see `inFlightRef`): one click = one
    // PATCH and exactly one `paused_clicked`. `submitting` alone (async state)
    // lets two same-tick clicks both through.
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    // Intent metric: emit on the click, AFTER the latch (so a double click
    // emits once) but BEFORE the PATCH, regardless of whether the PATCH later
    // succeeds (D8 plan: paused_clicked is a click event).
    const eventProps: Record<string, string> = {
      suggested_tier: suggestedTier.toLowerCase(),
      surface: 'banner',
    };
    if (acceptance?.workspaceBucket) {
      eventProps.workspace_bucket = acceptance.workspaceBucket;
    }
    trackEvent(LEAD_AGENT_PAYWALL_EVENTS.PAUSED_CLICKED, eventProps);

    setSubmitting(true);
    try {
      await pauseAcceptance();
      // Success: advance to the confirmation step. The banner already self-hid
      // (state is now PAUSED) but the container keeps the modal mounted.
      setStep('confirmed');
    } catch {
      // Stay on step 1 with an error toast: the banner is still visible and
      // nothing is lost, so the Owner can retry or back out.
      addAlert({ type: eAlertType.ERROR, message: t('decline.errors.pause') });
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  }

  function handleBackToDashboard() {
    // The row is already PAUSED; resolve it locally so the dashboard stays
    // clean for the rest of the session.
    markResolved();
    onClose();
  }

  const warnCopy = paymentDate
    ? t('decline.warning.body', { date: paymentDate })
    : t('decline.warning.bodyNoDate');
  const confirmCopy = paymentDate
    ? t('decline.confirmed.body', { date: paymentDate })
    : t('decline.confirmed.bodyNoDate');

  return (
    <Modal.Legacy
      open
      onClickBackdrop={onClose}
      className="LeadAgentDeclineModal rounded-lg p-0 min-w-[25rem] max-w-[25rem]"
    >
      {step === 'warning' ? (
        <div data-testid="lead-agent-decline-step1">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold">
              {t('decline.warning.title')}
            </h3>
            <XMarkIcon
              onClick={onClose}
              className="size-6 cursor-pointer text-neutral-500"
              data-testid="lead-agent-decline-close"
            />
          </div>

          <div className="p-4 flex flex-col gap-3">
            <p className="text-sm text-neutral-600 leading-relaxed">
              {warnCopy}
            </p>
            <ul className="flex flex-col gap-2 rounded bg-error/5 p-3 text-sm text-neutral-700">
              <li>{t('decline.warning.loss1')}</li>
              <li>{t('decline.warning.loss2')}</li>
              <li>{t('decline.warning.loss3')}</li>
            </ul>
          </div>

          <div className="LeadAgentDeclineModal__Actions p-4 bg-base-200 flex gap-3">
            <Button
              color="ghost"
              size="sm"
              className="rounded flex-1"
              onClick={() => {
                void handleDecline();
              }}
              disabled={submitting}
              loading={submitting}
              data-testid="lead-agent-decline-confirm"
            >
              {t('decline.warning.confirmCta')}
            </Button>
            <Button
              color="primary"
              size="sm"
              className="rounded flex-1"
              onClick={onClose}
              disabled={submitting}
              data-testid="lead-agent-decline-keep"
            >
              {t('decline.warning.keepCta')}
            </Button>
          </div>
        </div>
      ) : (
        <div data-testid="lead-agent-decline-step2">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold">
              {t('decline.confirmed.title')}
            </h3>
            <XMarkIcon
              onClick={handleBackToDashboard}
              className="size-6 cursor-pointer text-neutral-500"
              data-testid="lead-agent-decline-confirmed-close"
            />
          </div>

          <div className="p-4 flex items-start gap-3">
            <span className="flex items-center justify-center size-9 shrink-0 rounded-full bg-neutral-200 text-neutral-500">
              <XMarkIcon className="size-5" aria-hidden="true" />
            </span>
            <p className="text-sm text-neutral-600 leading-relaxed">
              {confirmCopy}
            </p>
          </div>

          <div className="LeadAgentDeclineModal__Actions p-4 bg-base-200 flex gap-3">
            <Button
              color="ghost"
              size="sm"
              className="rounded flex-1"
              onClick={onReactivate}
              data-testid="lead-agent-decline-reactivate"
            >
              {t('decline.confirmed.reactivateCta')}
            </Button>
            <Button
              color="primary"
              size="sm"
              className="rounded flex-1"
              onClick={handleBackToDashboard}
              data-testid="lead-agent-decline-back"
            >
              {t('decline.confirmed.backCta')}
            </Button>
          </div>
        </div>
      )}
    </Modal.Legacy>
  );
}
